import { useStaticQuery, graphql } from "gatsby"
import React, { useMemo } from "react"
import { isMobile } from "react-device-detect"

import MainInfoBlock from "components/MainInfoBlock"
import MainIllustration1 from "assets/svgs/main-1.svg"
import MainIllustration2 from "assets/svgs/main-2.svg"
import MainIllustration3 from "assets/svgs/main-3.svg"
import MainIllustration4 from "assets/svgs/main-4.svg"
import Mockup from "components/Mockup"

import Section from "components/ui/Section"
import { LandingPagesProps } from "types/interfaces"
import { LandingPageType } from "types/enums"
import BottomBanner from "components/BottomBanner"

interface MainInfoBlock {
  node: {
    id: string
    frontmatter: {
      subtitle: string
      reverse: boolean
      textParagraphs: string[]
    }
  }
}

interface MainInfoBlockObject {
  subtitle: string
  reverse: boolean
  textParagraphs: string[]
}

export const sectionQuery = graphql`
  query MainInfoBlocksData {
    allMarkdownRemark(
      sort: { fields: frontmatter___order }
      filter: { frontmatter: { category: { eq: "main info" } } }
    ) {
      edges {
        node {
          id
          frontmatter {
            textParagraphs
            subtitle
            reverse
          }
        }
      }
    }
  }
`

const vendorTextsForPartOne: MainInfoBlockObject[] = [
  {
    subtitle: "Discover seamless scheduling and confirmation",
    reverse: false,
    textParagraphs: [
      "There is no more need to worry about time-consuming COI management. Movezee helps you verify process status without making extra calls or sending e-mails. Accept and confirm COIs directly in the system. Simply and automatically.",
    ],
  },
  {
    subtitle: "How we solve it",
    reverse: true,
    textParagraphs: [
      "Scheduling and confirming COIs can be quite time-consuming. With our solution, you can count on simplified and standardized COI processes that let you complete your tasks faster and more efficiently.",
      "Thanks to Movezee, you can check the COI status at any time and provide acceptance or confirmation directly in the system. Forget about complicated procedures and long processes.",
      "Our solution will let you gather all the data in one place and stay in productive contact with the tenant, management company, and building administrators. Now, you can make all the inconveniences go away and manage COI as effortlessly as possible.",
    ],
  },
]

const MainInfoSection: React.FC<LandingPagesProps> = ({ landingType }) => {
  const { allMarkdownRemark } = useStaticQuery(sectionQuery)
  const infoBlocks: MainInfoBlock[] = allMarkdownRemark.edges
  const imageComponents = useMemo(
    () =>
      landingType === LandingPageType.MANAGEMENT_COMPANIES
        ? [<MainIllustration1 />, <MainIllustration2 />]
        : [<MainIllustration3 />, <MainIllustration4 />],
    [landingType]
  )

  const titles = [
    <p>
      {landingType === LandingPageType.MANAGEMENT_COMPANIES
        ? "From hours to couple of"
        : "Fast contact with a couple of"}{" "}
      <span className="lighter">clicks</span>
    </p>,
    <p>
      <span className="lighter">Simplified</span> and standardized process
    </p>,
    <p>
      Centralized system{isMobile ? <br /> : " "}with all data
      {isMobile ? <br /> : " "}
      in <span className="lighter">one place</span>
    </p>,
  ]

  const partOne = useMemo(
    () =>
      landingType === LandingPageType.MANAGEMENT_COMPANIES
        ? infoBlocks.slice(0, 2).map(
            (
              {
                node: {
                  frontmatter: { subtitle, reverse, textParagraphs },
                  id,
                },
              },
              index
            ) => (
              <MainInfoBlock
                key={id}
                subtitle={{ text: subtitle, lighter: true }}
                reverse={reverse}
                textParagraphs={textParagraphs}
                image={imageComponents[index]}
                title={titles[index]}
                landingType={landingType}
                isButtonHidden={textParagraphs.length === 1}
              />
            )
          )
        : vendorTextsForPartOne.map((item, index) => (
            <MainInfoBlock
              key={index}
              subtitle={{ text: item.subtitle, lighter: true }}
              reverse={item.reverse}
              textParagraphs={item.textParagraphs}
              image={imageComponents[index]}
              title={titles[index]}
              landingType={landingType}
              isButtonHidden={item.textParagraphs.length === 1}
            />
          )),
    [landingType]
  )

  const partTwo = infoBlocks.slice(2).map(
    ({
      node: {
        frontmatter: { subtitle, reverse, textParagraphs },
        id,
      },
    }) => (
      <MainInfoBlock
        key={id}
        subtitle={{ text: subtitle, lighter: true }}
        reverse={reverse}
        textParagraphs={textParagraphs}
        title={titles[2]}
        separateTitle={true}
        landingType={landingType}
        isButtonHidden={true}
      />
    )
  )

  return (
    <Section>
      {partOne}
      {/* TODO: Remove when testimonials will be ready */}
      <BottomBanner landingType={LandingPageType.MANAGEMENT_COMPANIES} />
      <Mockup />
      {partTwo}
    </Section>
  )
}

export default MainInfoSection
